import { V3Person } from './people';
import {V3School, V3SchoolManager, V3SchoolStudent, V3SchoolTeacher} from './schools';

export type NewsType = 'NEWS' | 'ADS' | 'BLOG' | 'NOTIFICATION';

export const NEWS_TYPES: NewsType[] = ['NEWS', 'ADS', 'BLOG', 'NOTIFICATION'];

export interface NewsRef {
  id: number;
}

export interface NewsContent {
  title: string;
  subtitle: string;
  labels: string;
  copy: string;
  /** Main (cover) image of the news; additional body images live in NewsItem.images. */
  image: string;
  type?: NewsType | null;
}

export interface NewsImageItem {
  id: number;
  url: string;
  sortOrder: number;
}

export interface NewsItem extends NewsRef {
  creationDate: string;
  publicationDate: string | null;
  author: V3Person;
  content: NewsContent;
  images?: NewsImageItem[];
  recipients?: NewsRecipient[];
}

export interface NewsListEntry extends NewsRef {
  newsItem: NewsItem;
  readingDate: string | null;
}

export interface NewsRecipientBase {
  '@type': string;
}

export interface NewsRecipientManager extends NewsRecipientBase {
  '@type': 'NewsRecipientManager';
  manager: V3SchoolManager;
}

export interface NewsRecipientSchoolManagers extends NewsRecipientBase {
  '@type': 'NewsRecipientSchoolManagers';
  learningUnit: V3School;
}

export interface NewsRecipientStudent extends NewsRecipientBase {
  '@type': 'NewsRecipientStudent';
  student: V3SchoolStudent;
}

export interface NewsRecipientTeacher extends NewsRecipientBase {
  '@type': 'NewsRecipientTeacher';
  teacher: V3SchoolTeacher;
}

export interface NewsRecipientSchoolStudents extends NewsRecipientBase {
  '@type': 'NewsRecipientSchoolStudents';
  learningUnit: V3School;
}

export interface NewsRecipientSchoolTeachers extends NewsRecipientBase {
  '@type': 'NewsRecipientSchoolTeachers';
  learningUnit: V3School;
}

export interface NewsRecipientAllManagers extends NewsRecipientBase {
  '@type': 'NewsRecipientAllManagers';
}

export interface NewsRecipientAllStudents extends NewsRecipientBase {
  '@type': 'NewsRecipientAllStudents';
}

export interface NewsRecipientAllTeachers extends NewsRecipientBase {
  '@type': 'NewsRecipientAllTeachers';
}

export type NewsRecipient =
  | NewsRecipientManager
  | NewsRecipientSchoolManagers
  | NewsRecipientStudent
  | NewsRecipientTeacher
  | NewsRecipientSchoolStudents
  | NewsRecipientSchoolTeachers
  | NewsRecipientAllManagers
  | NewsRecipientAllStudents
  | NewsRecipientAllTeachers;

export interface NewsRecipientReadingBase {
  '@type': string;
}

/** Reading entry of a single recipient; grouping entries have no reading date. */
export interface NewsRecipientReadingPersonBase extends NewsRecipientReadingBase {
  readingDate: string | null;
}

export interface NewsRecipientReadingManager extends NewsRecipientReadingPersonBase {
  '@type': 'NewsRecipientReadingManager';
  manager: V3SchoolManager;
}

export interface NewsRecipientReadingSchoolManagers extends NewsRecipientReadingBase {
  '@type': 'NewsRecipientReadingSchoolManagers';
  learningUnit: V3School;
  managers: NewsRecipientReadingManager[];
}

export interface NewsRecipientReadingStudent extends NewsRecipientReadingPersonBase {
  '@type': 'NewsRecipientReadingStudent';
  student: V3SchoolStudent;
}

export interface NewsRecipientReadingTeacher extends NewsRecipientReadingPersonBase {
  '@type': 'NewsRecipientReadingTeacher';
  teacher: V3SchoolTeacher;
}

export type NewsRecipientReading =
  | NewsRecipientReadingManager
  | NewsRecipientReadingSchoolManagers
  | NewsRecipientReadingStudent
  | NewsRecipientReadingTeacher;
